/**
 * useDraftAutosave — wires the PURE autosave machine (lib/draftAutosave) to
 * react-hook-form + the area API (PR-ONB-RESUME-1B, lock #4 + review fixes
 * #2/#3/#4/#5 + second-round fixes of 2026-06-12).
 *
 * Active ONLY when the resumable flag is on AND the session is bound; in
 * every other case the hook is inert and the wizard behaves byte-identical
 * to the legacy flow.
 *
 * STATE OWNERSHIP (second-round fix #1): the SYNCHRONOUS store
 * (`createSyncAutosaveStore`) is the authoritative state for every async
 * decision — React's useReducer/refs synced at render time lag behind
 * dispatches, so a drain loop reading them could reuse a stale revision
 * (artificial 409) or misread an in-flight status. Every event goes through
 * `store.dispatch`, which applies the reducer immediately AND forwards the
 * event to React's dispatch purely for the UI.
 *
 * RETRY POLICY (second-round fix #2):
 *   pending → automatic debounce save; error → NO automatic retry (the
 *   status line shows the failure); a new edit re-arms the autosave;
 *   "Riprova"/"Salva ed esci" perform ONE explicit attempt; the drain stops
 *   at the first failure.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import {
  DEFAULT_VALUES,
  type RegistrationFormValues,
} from '@/hooks/useRegistrationForm';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import {
  AUTOSAVE_DEBOUNCE_MS,
  autosaveReducer,
  canExplicitlyRetry,
  createSyncAutosaveStore,
  drainPendingSaves,
  initialAutosaveState,
  shouldAutosave,
  type AutosaveState,
  type SyncAutosaveStore,
} from '@/lib/draftAutosave';
import {
  buildDraftPayloadFromForm,
  draftStateForCurrentStep,
  WIZARD_STEP_ORDER,
  type WizardStepKey,
} from '@/lib/accountBindFlow';
import { hydrateDraftOverDefaults } from '@/lib/draftHydration';
import { getAreaRequest, saveAreaDraft } from '@/lib/areaApi';

function stepKeyFromPathname(pathname: string): WizardStepKey | null {
  const last = pathname.split('/').filter(Boolean).pop() ?? '';
  return (WIZARD_STEP_ORDER as readonly string[]).includes(last)
    ? (last as WizardStepKey)
    : null;
}

export interface DraftAutosaveHandle {
  state: AutosaveState;
  active: boolean;
  /**
   * Explicit flush ("Salva ed esci" / "Riprova"): performs at most ONE
   * explicit retry from an error state, then drains genuinely buffered work,
   * stopping at the first failure. Resolves true only when the machine is
   * clean — the caller must NOT navigate on false.
   */
  flushNow: () => Promise<boolean>;
  /** Hide the persistent conflict notice (review fix #4). */
  dismissConflictNotice: () => void;
}

export function useDraftAutosave(): DraftAutosaveHandle {
  const session = useAreaSession();
  const { getToken, setDraftRevision } = session;
  const bindPublicId = session.bind?.requestPublicId ?? null;
  const bindRevision = session.bind?.draftRevision ?? null;
  const form = useFormContext<RegistrationFormValues>();
  const location = useLocation();
  const active = session.enabled && bindPublicId !== null;

  // UI state: mirrors the sync store one render behind — display only.
  const [uiState, dispatchUi] = useReducer(
    autosaveReducer,
    initialAutosaveState(bindRevision ?? 0, bindPublicId),
  );
  // Authoritative SYNCHRONOUS store (second-round fix #1). The ref holds the
  // single instance; onEvent forwards to React for the UI re-render.
  const storeRef = useRef<SyncAutosaveStore | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createSyncAutosaveStore(
      initialAutosaveState(bindRevision ?? 0, bindPublicId),
      dispatchUi,
    );
  }
  const store = storeRef.current;

  const stepRef = useRef<WizardStepKey | null>(stepKeyFromPathname(location.pathname));
  stepRef.current = stepKeyFromPathname(location.pathname);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  // Suspends the form watcher while WE reset the form from the server: the
  // hydration must never register as a new user edit (review fix #4).
  const suppressWatchRef = useRef(false);

  // Late bind / re-bind sync (review fix #3): re-seed requestPublicId +
  // revision when the bind lands/changes after mount.
  useEffect(() => {
    if (bindPublicId === null || bindRevision === null) return;
    store.dispatch({
      type: 'BIND_SYNCED',
      requestPublicId: bindPublicId,
      revision: bindRevision,
    });
  }, [bindPublicId, bindRevision, store]);

  /**
   * One save round: awaits the in-flight request, or fires one if due.
   * `explicit` widens the gate to the error state (single user-driven retry).
   */
  const runSaveRound = useCallback(
    async (explicit = false): Promise<void> => {
      if (inFlightRef.current) {
        await inFlightRef.current;
        return;
      }
      const gate = explicit ? canExplicitlyRetry : shouldAutosave;
      if (!gate(store.getState())) return;

      const doSave = async (): Promise<void> => {
        store.dispatch({ type: 'SAVE_STARTED' });
        const token = await getToken();
        if (!token) {
          store.dispatch({ type: 'SAVE_FAILED' });
          return;
        }
        const step = stepRef.current ?? 'organizzazione';
        const result = await saveAreaDraft(token, {
          expectedRevision: store.getState().revision,
          draftPayload: buildDraftPayloadFromForm(
            form.getValues() as unknown as Record<string, unknown>,
          ),
          // The step being edited is NOT completed (review fix #2).
          draftState: draftStateForCurrentStep(step),
        });
        if (result.ok) {
          store.dispatch({
            type: 'SAVE_OK',
            revision: result.data.draftRevision,
            lastSavedAt: result.data.lastSavedAt,
          });
          setDraftRevision(result.data.draftRevision);
          return;
        }
        if (result.kind === 'DRAFT_CONFLICT') {
          store.dispatch({
            type: 'SAVE_CONFLICT',
            ...(result.currentRevision !== undefined && {
              currentRevision: result.currentRevision,
            }),
          });
          // Never overwrite the server: reload its version and hydrate with
          // the watcher suspended. The conflict NOTICE persists past the
          // technical resolution (reducer keeps conflictNotice true).
          const fresh = await getAreaRequest(token);
          if (fresh.ok) {
            suppressWatchRef.current = true;
            form.reset(
              hydrateDraftOverDefaults(
                DEFAULT_VALUES as unknown as Record<string, unknown>,
                fresh.data.draftPayload,
              ) as unknown as RegistrationFormValues,
            );
            queueMicrotask(() => {
              suppressWatchRef.current = false;
            });
            store.dispatch({
              type: 'SERVER_RELOADED',
              revision: fresh.data.draftRevision,
            });
            setDraftRevision(fresh.data.draftRevision);
          }
          return;
        }
        if (result.kind === 'NETWORK') {
          store.dispatch({ type: 'WENT_OFFLINE' });
          return;
        }
        store.dispatch({ type: 'SAVE_FAILED' });
      };

      const promise = doSave().finally(() => {
        inFlightRef.current = null;
      });
      inFlightRef.current = promise;
      await promise;
    },
    [form, getToken, setDraftRevision, store],
  );

  /**
   * Debounced AUTOMATIC round: saves only `pending` (never retries errors —
   * second-round fix #2) and reschedules only for changes buffered during a
   * SUCCESSFUL request.
   */
  const runDebouncedSave = useCallback(async (): Promise<void> => {
    await runSaveRound(false);
    if (shouldAutosave(store.getState())) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        () => void runDebouncedSave(),
        AUTOSAVE_DEBOUNCE_MS,
      );
    }
  }, [runSaveRound, store]);

  // Form watcher → CHANGED + debounce.
  useEffect(() => {
    if (!active) return;
    const subscription = form.watch(() => {
      if (suppressWatchRef.current) return; // server hydration, not an edit
      store.dispatch({ type: 'CHANGED' });
      if (!shouldAutosave(store.getState())) return; // parked (offline/conflict)
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        () => void runDebouncedSave(),
        AUTOSAVE_DEBOUNCE_MS,
      );
    });
    return () => {
      subscription.unsubscribe();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [active, form, runDebouncedSave, store]);

  const flushNow = useCallback(async (): Promise<boolean> => {
    if (!active) return true;
    if (timerRef.current) clearTimeout(timerRef.current);
    // ONE explicit retry per user action when sitting on an error.
    if (store.getState().status === 'error') {
      await runSaveRound(true);
    }
    return drainPendingSaves({
      getState: store.getState,
      runSave: () => runSaveRound(false),
    });
  }, [active, runSaveRound, store]);

  // Step change → best-effort drain of PENDING work (stops on error; the
  // status line keeps the failure visible).
  useEffect(() => {
    if (!active) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    void drainPendingSaves({
      getState: store.getState,
      runSave: () => runSaveRound(false),
    });
    // location.pathname is the trigger by design.
  }, [active, location.pathname, runSaveRound, store]);

  // Offline / online.
  useEffect(() => {
    if (!active) return;
    const offline = (): void => store.dispatch({ type: 'WENT_OFFLINE' });
    const online = (): void => {
      store.dispatch({ type: 'BACK_ONLINE' });
      void runDebouncedSave();
    };
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
    };
  }, [active, runDebouncedSave, store]);

  const dismissConflictNotice = useCallback(
    () => store.dispatch({ type: 'CONFLICT_NOTICE_DISMISSED' }),
    [store],
  );

  return { state: uiState, active, flushNow, dismissConflictNotice };
}

// ─── Shared handle (PR-ONB-PRECHECK-3) ───────────────────────────────────────
//
// ONE machine per wizard tree: the RegistrationLayout mounts the provider
// (inside its FormProvider) and BOTH consumers — the status line and the
// SummaryPage submit — read the SAME instance. A second `useDraftAutosave()`
// call would create a second watcher + saver and the two would 409 each
// other on the draft revision.

const DraftAutosaveContext = createContext<DraftAutosaveHandle | null>(null);

export function DraftAutosaveProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const handle = useDraftAutosave();
  return (
    <DraftAutosaveContext.Provider value={handle}>
      {children}
    </DraftAutosaveContext.Provider>
  );
}

/** Null outside the wizard tree (e.g. area pages) — callers must branch. */
export function useDraftAutosaveHandle(): DraftAutosaveHandle | null {
  return useContext(DraftAutosaveContext);
}
